import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faFileContract, faShieldAlt, faUserClock
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/Claims-Processing-Process.png";
import vis from "../../assets/images/use-cases/Claims-Processing-Dashboard.png";

const ClaimsProcessingAI = () => {
    return (
        <>
            <SEO
                title="AI-Powered Claims Processing: Digital Twin for Insurance | Nebulyx AI"
                description="Reduce claims processing time by 70% while improving accuracy. Combine document extraction, damage assessment, fraud detection, and settlement recommendations in one intelligent workbench."
                keywords="AI digital twin, claims processing, insurance, fraud detection, document extraction, settlement automation, claims workbench"
                canonicalUrl="https://nebulyx.ai/use-cases/claims-processing-ai"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faFileContract}/>
                                            &nbsp;
                                            Finance
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;How can we reduce claims processing time by 70% while improving accuracy?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        An intelligent claims workbench for a property insurance company, that combines document extraction, damage assessment, fraud detection, and settlement recommendations-enabling claims officers to process more claims with greater consistency.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faUserClock} className="stat-icon"/>
                                <span className="stat-value">70%</span>
                                <span className="stat-label">Faster Processing</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">95%</span>
                                <span className="stat-label">Extraction Accuracy</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faShieldAlt} className="stat-icon"/>
                                <span className="stat-value">3x</span>
                                <span className="stat-label">Fraud Detection Rate</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>Manual document review takes hours per claim</li>
                                            <li>Inconsistent damage assessments across officers</li>
                                            <li>Fraud detection relies on gut instinct, not data</li>
                                            <li>Settlement calculations error-prone and slow</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>AI document extractor processes forms, photos, reports</li>
                                            <li>Automated damage assessment with confidence scores</li>
                                            <li>Real-time fraud scoring with explainable indicators</li>
                                            <li>One-click settlement with full audit trail</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Claims officers process 3x more claims per day</li>
                                            <li>Consistent decisions improve customer satisfaction</li>
                                            <li>Fraud losses reduced by identifying suspicious patterns</li>
                                            <li>Full audit trail for regulatory compliance</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Connects policy data, claim submissions, historical patterns, and external data into a unified Data Object Graph that powers intelligent automation at every step of the claims workflow.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Extract & Validate</strong>
                                    <span>AI extracts data from documents, photos, and forms</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Assess & Score</strong>
                                    <span>Parallel damage assessment, policy check, and fraud analysis</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Recommend & Review</strong>
                                    <span>AI generates settlement recommendation with full reasoning</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Decide & Settle</strong>
                                    <span>One-click approval with automated payment processing</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Ingests data from policy systems, claim submissions, historical claims, and external sources.
                                            AI models extract documents, assess damage, check policy eligibility, and detect fraud
                                            in parallel-producing a recommended decision with full audit trail.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Claims Officer Workbench</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Unified interface showing prioritised work queue, extracted document data with confidence scores,
                                            damage breakdown, policy eligibility, fraud risk indicators, and AI-recommended settlement.
                                            Officers approve, deny, or escalate claims with a single click.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>


                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Policy Data & Coverage Terms</li>
                                            <li>Claim Submissions & Documents</li>
                                            <li>Photos, Receipts & Reports</li>
                                            <li>Historical Claims & Outcomes</li>
                                            <li>External Data (Weather, Accidents)</li>
                                            <li>Fraud Intelligence Patterns</li>
                                            <li>Settlement History</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Document extractor (LLM-powered)</li>
                                            <li>Damage assessment engine</li>
                                            <li>Policy eligibility matcher</li>
                                            <li>Fraud detection scorer</li>
                                            <li>Settlement calculator</li>
                                            <li>Customer email generator</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Claim Types Supported</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Auto Collision - end-to-end</li>
                                            <li>Property Damage - weather events</li>
                                            <li>Theft Claims - enhanced fraud checks</li>
                                            <li>Liability Claims - multi-party</li>
                                            <li>Bulk Processing - catastrophic events</li>
                                            <li>Appeal Handling - denied claims</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Discovery</h3>
                                    <p>
                                        By modelling the claims workflow as a <strong>Data Object Graph</strong>, we can connect
                                        policy data, submissions, and historical patterns into enriched claim profiles.
                                        <strong> The AI engine transforms this unified view into intelligent recommendations-from
                                            document extraction to fraud scoring to settlement calculation</strong>
                                        {" "}-while maintaining full explainability and audit trails for every decision,
                                        keeping humans in control while dramatically accelerating processing.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Optimal Strategy Found</span>
                                        <span className="discovery-value">AI-Assisted Decisions</span>
                                        <span className="discovery-note">70% faster, 95% accurate, 3x fraud detection</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Want to Transform Your Claims Operation?</h2>
                            <p>
                                If your claims processing is slow, inconsistent, or missing fraud,
                                let's build your AI-powered claims Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default ClaimsProcessingAI;